/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, createContext } from "react";
import { useParams, NavLink } from "react-router-dom";
import UploadIRModal from "../../components/Modals/UploadIRModal";
import AllReports from "./AllReports";
import "./interrogationList.scss";

export const WrapChatContext = createContext({});

const InterrogationList = () => {
  const [checkedItems, setCheckedItems] = useState([]);
  const [acceptBatchSign, setAcceptBatchSign] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { tab } = useParams();
  const [showPopup, setShowPopup] = useState(true);

  const popupModalValidation = () => {
    return showPopup && sessionStorage.getItem("showModalRetireApp") ? (
      <div className="modal override-row popup show" id="modalAgreements">
        <div className="modalContent">
          <div className="grpHdr">
            <h2>IMPORTANT NOTICE </h2>
          </div>
          <p>
            <b>
              Effective June 30, 2025, Philips will be ending provision of the services and products related
              to its Implantable Device Monitoring Business (“Geneva”).
            </b>
            <br />
            <br />
            With the closure of Geneva, continued servicing of your patients will become the responsibility of
            your clinic. Therefore, your action is required. A letter with detailed information has been
            mailed to your clinic. In case of questions, please contact customersuccess@philips.com. <br />{" "}
            <br /> Please click “OK” to acknowledge and continue.
          </p>
          <div className="row rowStretch noMargin">
            <button
              className="btn btn-sm btn-teal"
              onClick={() => {
                sessionStorage.removeItem("showModalRetireApp");
                setShowPopup(false);
              }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    ) : null;
  };

  const onBatchSign = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("genToken") && JSON.parse(localStorage.getItem("genToken")).token;
      const response = await fetch(
        `/patientmanagement/rest/interrogations/batchSign?ids=${checkedItems.join(",")}`,
        {
          method: "PUT",
          headers: {
            token,
            "Content-Type": "application/json",
          },
          body: {},
        }
      );
      if (response.ok) {
        setIsLoading(false);
        window.location.replace(window.location.href.split("#")[0]);
      }
    } catch (error) {
      setIsLoading(false);
      console.log("ERR", error);
    }
  };

  useEffect(() => {
    if (!tab) {
      localStorage.setItem("selectedTab", "myReports");
    } else {
      localStorage.setItem("selectedTab", tab);
    }
  }, [tab]);

  const classNameTab = (pos) => {
    if (pos === tab || (pos === "myReports" && !tab)) {
      return "tabActive";
    }
  };

  return (
    <div id="index" className="containerFlex maxHd interrogationList">
      <div className="contentArea">
        <div className="up-interrog" style={{ display: "flex", justifyContent: "flex-end" }}>
          {checkedItems.some((i) => i) ? (
            <a href="#batch">
              <button className="btn btnUrgent">Batch Sign</button>
            </a>
          ) : null}
          <a href="#uploadIR" id="upload-report-btn">
            <button
              className="btn btnSuccess"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div className="upload-icon icon-64" style={{ marginRight: "10px" }}></div> Upload Report
            </button>
          </a>
        </div>

        <nav className="tabs" id="report-navigation">
          <NavLink
            to="/reports3"
            id="myReports-tab"
            className={classNameTab("myReports")}
            onClick={() => localStorage.setItem("selectedTab", "myReports")}
          >
            <span className="tab-text">My Reports</span>
          </NavLink>
          <NavLink
            to="/reports3/poc"
            id="poc-tab"
            className={classNameTab("poc")}
            onClick={() => localStorage.setItem("selectedTab", "poc")}
          >
            <span className="tab-text">My POC</span>
          </NavLink>
          <NavLink
            to="/reports3/remote"
            id="remote-tab"
            className={classNameTab("remote")}
            onClick={() => localStorage.setItem("selectedTab", "remote")}
          >
            <span className="tab-text">My Remote</span>
          </NavLink>
          <NavLink
            to="/reports3/all"
            id="allReports-tab"
            className={classNameTab("all")}
            onClick={() => localStorage.setItem("selectedTab", "all")}
          >
            <span className="tab-text">All Reports</span>
          </NavLink>
          <NavLink
            to="/reports3/unprocessed"
            id="unProcessed-tab"
            className={classNameTab("unprocessed")}
            onClick={() => localStorage.setItem("selectedTab", "unprocessed")}
          >
            <span className="tab-text">Unprocessed</span>
          </NavLink>
          <NavLink
            to="/reports3/followup"
            id="followup-tab"
            className={classNameTab("followup")}
            onClick={() => localStorage.setItem("selectedTab", "followup")}
          >
            <span className="tab-text">Follow Up</span>
          </NavLink>
          <NavLink
            to="/reports3/biotel"
            id="biotel-tab"
            className={classNameTab("biotel")}
            onClick={() => localStorage.setItem("selectedTab", "biotel")}
          >
            <span className="tab-text">Wearables</span>
          </NavLink>
        </nav>

        <div className="tabContent dashboard-reports">
          <AllReports
            activeTab={tab || "myReports"}
            setCheckedItems={setCheckedItems}
            checkedItems={checkedItems}
          />
        </div>

        {/* Batch Sign Modal */}
        <div className="modal override-row up-interrog popup" id="batch">
          <div className="modalContent">
            <div className="grpHdr">
              <h3>Batch Sign Interrogations</h3>
            </div>
            <div className="row rowStretch">
              <div className="col col-s-100 col100 colFlex0">
                <p style={{ lineHeight: "28px" }}>
                  {`User acknowledges and agrees that Geneva Health Solutions (GHS) [Clinic Platform] provides
                  the User with the ability to view and sign multiple health record entries at one time
                  ("Batch Signing"). By clicking "I accept the terms and conditions of batch signing" in the
                  checkbox below, User (i) affirms that User has read and understands all instructions for the
                  use of Batch Signing, including the method for adding and removing an entry from a batch,
                  viewing and editing individual entries within a batch, and applying User's electronic
                  signature to the batch, (ii) agrees that User shall be solely responsible for reviewing,
                  editing as necessary, and validating each individual entry prior to Batch Signing, and for
                  ensuring that User's medical documentation, review and attestation method complies with all
                  applicable laws, regulations, payer rules, facility protocols and accreditation standards
                  (including any rules governing electronic signatures and batch signing of medical records),
                  (iii) assumes all responsibility for all costs, damages, claims or other liabilities that
                  may result from or relate to the use of Batch Signing, including without limitation
                  documentation errors, or improper authentication or signing of records, and (iv) agrees that
                  GHS shall have no responsibility or liability for any such costs, damages, claims or other
                  liabilities.`}
                </p>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <label className="formLi" style={{ width: "20px", marginRight: "10px" }}>
                    <input
                      type="checkbox"
                      className="chk"
                      checked={acceptBatchSign}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setAcceptBatchSign(checked);
                      }}
                    />
                    <span className="chkmrk"></span>
                  </label>
                  I accept the terms and conditions of batch signing.
                </div>
              </div>
            </div>
            <div className="row rowStretch noMargin">
              <div className="col create-ir-col" style={{ display: "flex" }}>
                <button
                  className="btn btnSuccess"
                  onClick={onBatchSign}
                  disabled={!acceptBatchSign || isLoading}
                >
                  {isLoading ? (
                    <div
                      id="results-loading-icon"
                      className="saving-white-ico icon-64"
                      style={{ width: 15, height: 15, marginRight: 3 }}
                    />
                  ) : null}
                  OK
                </button>
                <a href="#">
                  <button className="btn btn-cancel">Cancel</button>
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* Batch Sign Modal */}

        {/* Upload Report Modal */}
        <UploadIRModal />
        {/* Upload Report Modal */}

        <a href="#" className="close-popup"></a>
      </div>
      {popupModalValidation()}
    </div>
  );
};

export default InterrogationList;
